const DB_NAME = 'library-offline';
const DB_VERSION = 2;
const EPUB_STORE = 'epubs';
const PROGRESS_STORE = 'reading-progress';
const SETTINGS_STORE = 'reader-settings';

interface OfflineBook {
  id: number;
  title: string;
  epubData: ArrayBuffer;
  coverUrl?: string;
  savedAt: number;
}

export interface ReadingProgress {
  bookId: number;
  cfi: string;
  percentage: number;
  updatedAt: number;
}

export interface ReaderSettings {
  id: number | 'global';
  fontSize: number;
  fontFamily:
    | 'default'
    | 'serif'
    | 'sans-serif'
    | 'opendyslexic'
    | 'fast-sans'
    | 'fast-serif'
    | 'fast-sans-dotted'
    | 'fast-mono'
    | 'fast-opendyslexic';
  lineHeight: number;
  theme: 'light' | 'sepia' | 'dark';
  updatedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(EPUB_STORE)) {
        const epubStore = db.createObjectStore(EPUB_STORE, { keyPath: 'id' });
        epubStore.createIndex('savedAt', 'savedAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(PROGRESS_STORE)) {
        const progressStore = db.createObjectStore(PROGRESS_STORE, {
          keyPath: 'bookId',
        });
        progressStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        const settingsStore = db.createObjectStore(SETTINGS_STORE, {
          keyPath: 'id',
        });
        settingsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

export async function saveBookOffline(
  id: number,
  title: string,
  epubData: ArrayBuffer,
  coverUrl?: string
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(EPUB_STORE, 'readwrite');
    const store = tx.objectStore(EPUB_STORE);

    const book: OfflineBook = {
      id,
      title,
      epubData,
      coverUrl,
      savedAt: Date.now(),
    };

    const request = store.put(book);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    tx.oncomplete = () => db.close();
  });
}

export async function getOfflineBook(id: number): Promise<OfflineBook | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(EPUB_STORE, 'readonly');
    const store = tx.objectStore(EPUB_STORE);

    const request = store.get(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ?? null);

    tx.oncomplete = () => db.close();
  });
}

export async function removeOfflineBook(id: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(EPUB_STORE, 'readwrite');
    const store = tx.objectStore(EPUB_STORE);

    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    tx.oncomplete = () => db.close();
  });
}

export async function getOfflineBooks(): Promise<OfflineBook[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(EPUB_STORE, 'readonly');
    const store = tx.objectStore(EPUB_STORE);

    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ?? []);

    tx.oncomplete = () => db.close();
  });
}

export async function isBookSavedOffline(id: number): Promise<boolean> {
  const book = await getOfflineBook(id);
  return book !== null;
}

export async function saveReadingProgress(
  bookId: number,
  cfi: string,
  percentage: number
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROGRESS_STORE, 'readwrite');
    const store = tx.objectStore(PROGRESS_STORE);

    const progress: ReadingProgress = {
      bookId,
      cfi,
      percentage,
      updatedAt: Date.now(),
    };

    const request = store.put(progress);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();

    tx.oncomplete = () => db.close();
  });
}

export async function getReadingProgress(
  bookId: number
): Promise<ReadingProgress | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROGRESS_STORE, 'readonly');
    const store = tx.objectStore(PROGRESS_STORE);

    const request = store.get(bookId);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ?? null);

    tx.oncomplete = () => db.close();
  });
}

export async function getOfflineStorageUsage(): Promise<{
  bookCount: number;
  totalSize: number;
}> {
  const books = await getOfflineBooks();
  const totalSize = books.reduce((acc, book) => acc + book.epubData.byteLength, 0);
  return {
    bookCount: books.length,
    totalSize,
  };
}

export async function getAllReadingProgress(): Promise<ReadingProgress[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PROGRESS_STORE, 'readonly');
    const store = tx.objectStore(PROGRESS_STORE);

    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ?? []);

    tx.oncomplete = () => db.close();
  });
}

export const DEFAULT_READER_SETTINGS: Omit<ReaderSettings, 'id' | 'updatedAt'> = {
  fontSize: 100,
  fontFamily: 'default',
  lineHeight: 1.6,
  theme: 'dark',
};

export async function saveReaderSettings(
  id: number | 'global',
  settings: Partial<Omit<ReaderSettings, 'id' | 'updatedAt'>>
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SETTINGS_STORE, 'readwrite');
    const store = tx.objectStore(SETTINGS_STORE);

    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const existing = getRequest.result as ReaderSettings | undefined;
      const newSettings: ReaderSettings = {
        ...DEFAULT_READER_SETTINGS,
        ...existing,
        ...settings,
        id,
        updatedAt: Date.now(),
      };

      const putRequest = store.put(newSettings);
      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => resolve();
    };
    getRequest.onerror = () => reject(getRequest.error);

    tx.oncomplete = () => db.close();
  });
}

export async function getReaderSettings(
  id: number | 'global'
): Promise<ReaderSettings | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SETTINGS_STORE, 'readonly');
    const store = tx.objectStore(SETTINGS_STORE);

    const request = store.get(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ?? null);

    tx.oncomplete = () => db.close();
  });
}

export async function getEffectiveReaderSettings(
  bookId: number
): Promise<Omit<ReaderSettings, 'id' | 'updatedAt'>> {
  const [bookSettings, globalSettings] = await Promise.all([
    getReaderSettings(bookId),
    getReaderSettings('global'),
  ]);

  return {
    ...DEFAULT_READER_SETTINGS,
    ...globalSettings,
    ...bookSettings,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
